import React, { useCallback, useContext, useMemo } from 'react';
import { Context } from '../../../context/global-data-provider';
import { convertPriceToNumber } from '../../../utils/numbers';
import { BtnShareRates } from '../../components/btn-share-rates';

const useListWRates = () => {
  const { options } = useContext(Context);

  const prices = useMemo(() => {
    return {
      rate_new_design_coin: convertPriceToNumber(options.wrate_new_design_coin),
      rate_half_coin: convertPriceToNumber(options.wrate_half_coin),
      rate_quarter_coin: convertPriceToNumber(options.wrate_quarter_coin),
      rate_1_gram_coin: convertPriceToNumber(options.wrate_1_gram_coin),
      rate_ounces_of_gold: convertPriceToNumber(options.wrate_ounces_of_gold),
      rate_quote_of_tehran: convertPriceToNumber(options.wrate_quote_of_tehran),
      rate_18_carat_gold: convertPriceToNumber(options.wrate_18_carat_gold),

      ratec_new_design_coin: convertPriceToNumber(options.wratec_new_design_coin),
      ratec_half_coin: convertPriceToNumber(options.wratec_half_coin),
      ratec_quarter_coin: convertPriceToNumber(options.wratec_quarter_coin),
      ratec_1_gram_coin: convertPriceToNumber(options.wratec_1_gram_coin),
      ratec_ounces_of_gold: convertPriceToNumber(options.wratec_ounces_of_gold),
      ratec_quote_of_tehran: convertPriceToNumber(options.wratec_quote_of_tehran),
      ratec_18_carat_gold: convertPriceToNumber(options.wratec_18_carat_gold),

      raten_new_design_coin: convertPriceToNumber(options.wraten_new_design_coin),
      raten_half_coin: convertPriceToNumber(options.wraten_half_coin),
      raten_quarter_coin: convertPriceToNumber(options.wraten_quarter_coin),
      raten_1_gram_coin: convertPriceToNumber(options.wraten_1_gram_coin),
      raten_ounces_of_gold: convertPriceToNumber(options.wraten_ounces_of_gold),
      raten_quote_of_tehran: convertPriceToNumber(options.wraten_quote_of_tehran),
      raten_18_carat_gold: convertPriceToNumber(options.wraten_18_carat_gold),
      raten_other_golds: parseFloat(options.wraten_other_golds),
      raten_molten: convertPriceToNumber(options.wraten_molten),

      rates_new_design_coin: convertPriceToNumber(options.wrates_new_design_coin),
      rates_half_coin: convertPriceToNumber(options.wrates_half_coin),
      rates_quarter_coin: convertPriceToNumber(options.wrates_quarter_coin),
      rates_1_gram_coin: convertPriceToNumber(options.wrates_1_gram_coin),
      rates_ounces_of_gold: convertPriceToNumber(options.wrates_ounces_of_gold),
      rates_quote_of_tehran: convertPriceToNumber(options.wrates_quote_of_tehran),
      rates_18_carat_gold: convertPriceToNumber(options.wrates_18_carat_gold),
    };
  }, [options]);

  // Helper function to select appropriate price
  const selectPrice = useCallback(
    (rateName, ratecName) => {
      const ratePrice = prices[rateName];
      const ratecPrice = prices[ratecName];

      if (ratecPrice && ratecPrice.price > 0) {
        return ratecPrice;
      }

      return ratePrice;
    },
    [prices],
  );

  // Utility: Calculate buy price (main - buy diff)
  const calculateBuyPrice = (mainPrice, buyDiff) => {
    if (!mainPrice || !buyDiff) return mainPrice;
    return convertPriceToNumber(mainPrice.price - buyDiff.price);
  };
  // Utility: Calculate sell price (main + sell diff)
  const calculateSellPrice = (mainPrice, sellDiff) => {
    if (!mainPrice || !sellDiff) return mainPrice;
    return convertPriceToNumber(mainPrice.price + sellDiff.price);
  };

  // سکه‌ها
  const newDesignCoinMain = useMemo(() => selectPrice('rate_new_design_coin', 'ratec_new_design_coin'), [selectPrice]);
  const halfCoinMain = useMemo(() => selectPrice('rate_half_coin', 'ratec_half_coin'), [selectPrice]);
  const quarterCoinMain = useMemo(() => selectPrice('rate_quarter_coin', 'ratec_quarter_coin'), [selectPrice]);
  const oneGramCoinMain = useMemo(() => selectPrice('rate_1_gram_coin', 'ratec_1_gram_coin'), [selectPrice]);
  // طلاها
  const ouncesOfGoldMain = useMemo(() => selectPrice('rate_ounces_of_gold', 'ratec_ounces_of_gold'), [selectPrice]);
  const quoteTehranMain = useMemo(() => selectPrice('rate_quote_of_tehran', 'ratec_quote_of_tehran'), [selectPrice]);
  const eighteenCaratGoldMain = useMemo(() => selectPrice('rate_18_carat_gold', 'ratec_18_carat_gold'), [selectPrice]);

  // قیمت خرید و فروش برای هر آیتم
  const newDesignCoinBuy = useMemo(
    () => calculateBuyPrice(newDesignCoinMain, prices.raten_new_design_coin),
    [newDesignCoinMain, prices.raten_new_design_coin],
  );
  const newDesignCoinSell = useMemo(
    () => calculateSellPrice(newDesignCoinMain, prices.rates_new_design_coin),
    [newDesignCoinMain, prices.rates_new_design_coin],
  );

  const halfCoinBuy = useMemo(
    () => calculateBuyPrice(halfCoinMain, prices.raten_half_coin),
    [halfCoinMain, prices.raten_half_coin],
  );
  const halfCoinSell = useMemo(
    () => calculateSellPrice(halfCoinMain, prices.rates_half_coin),
    [halfCoinMain, prices.rates_half_coin],
  );

  const quarterCoinBuy = useMemo(
    () => calculateBuyPrice(quarterCoinMain, prices.raten_quarter_coin),
    [quarterCoinMain, prices.raten_quarter_coin],
  );
  const quarterCoinSell = useMemo(
    () => calculateSellPrice(quarterCoinMain, prices.rates_quarter_coin),
    [quarterCoinMain, prices.rates_quarter_coin],
  );

  const oneGramCoinBuy = useMemo(
    () => calculateBuyPrice(oneGramCoinMain, prices.raten_1_gram_coin),
    [oneGramCoinMain, prices.raten_1_gram_coin],
  );
  const oneGramCoinSell = useMemo(
    () => calculateSellPrice(oneGramCoinMain, prices.rates_1_gram_coin),
    [oneGramCoinMain, prices.rates_1_gram_coin],
  );

  const ouncesOfGoldBuy = useMemo(
    () => calculateBuyPrice(ouncesOfGoldMain, prices.raten_ounces_of_gold),
    [ouncesOfGoldMain, prices.raten_ounces_of_gold],
  );
  const ouncesOfGoldSell = useMemo(
    () => calculateSellPrice(ouncesOfGoldMain, prices.rates_ounces_of_gold),
    [ouncesOfGoldMain, prices.rates_ounces_of_gold],
  );

  const quoteTehranBuy = useMemo(
    () => calculateBuyPrice(quoteTehranMain, prices.raten_quote_of_tehran),
    [quoteTehranMain, prices.raten_quote_of_tehran],
  );
  const quoteTehranSell = useMemo(
    () => calculateSellPrice(quoteTehranMain, prices.rates_quote_of_tehran),
    [quoteTehranMain, prices.rates_quote_of_tehran],
  );

  const eighteenCaratGoldBuy = useMemo(
    () => calculateBuyPrice(eighteenCaratGoldMain, prices.raten_18_carat_gold),
    [eighteenCaratGoldMain, prices.raten_18_carat_gold],
  );
  const eighteenCaratGoldSell = useMemo(
    () => calculateSellPrice(eighteenCaratGoldMain, prices.rates_18_carat_gold),
    [eighteenCaratGoldMain, prices.rates_18_carat_gold],
  );

  // طلای متفرقه (خرید فقط)
  const otherGoldPrice = useMemo(() => {
    if (eighteenCaratGoldMain && prices.raten_other_golds) {
      const pn = (eighteenCaratGoldMain.price * parseFloat(prices.raten_other_golds)) / 100;
      return convertPriceToNumber(eighteenCaratGoldMain.price - pn);
    }

    return '-';
  }, [eighteenCaratGoldMain, prices]);

  // آبشده نقدی
  const moltenGoldPrice = useMemo(
    () =>
      prices.raten_molten.price === 0 || !prices.raten_molten.price
        ? 0
        : calculateBuyPrice(eighteenCaratGoldMain, prices.raten_molten),
    [eighteenCaratGoldMain, prices.raten_molten],
  );

  // نمایش سکه‌ها
  const coins = useMemo(() => {
    return (
      <>
        <div className="row-item f-row p-4p px-16p">
          <div className="item-cell text-right font-size-5">تمام سکه</div>
          <div className="item-cell text-center font-size-5 letter-spacing-1">{newDesignCoinBuy.formatted}</div>
          <div className="item-cell text-left font-size-5 letter-spacing-1">{newDesignCoinSell.formatted}</div>
        </div>

        <div className="row-item p-4p px-16p f-row justify-content-between">
          <div className="item-cell text-right font-size-5">نیم سکه</div>
          <div className="item-cell text-center font-size-5 letter-spacing-1">{halfCoinBuy.formatted}</div>
          <div className="item-cell text-left font-size-5 letter-spacing-1">{halfCoinSell.formatted}</div>
        </div>

        <div className="row-item p-4p px-16p f-row justify-content-between">
          <div className="item-cell text-right font-size-5">ربع سکه</div>
          <div className="item-cell text-center font-size-5 letter-spacing-1">{quarterCoinBuy.formatted}</div>
          <div className="item-cell text-left font-size-5 letter-spacing-1">{quarterCoinSell.formatted}</div>
        </div>

        <div className="row-item p-4p px-16p f-row justify-content-between">
          <div className="item-cell text-right font-size-5">سکه یک گرمی</div>
          <div className="item-cell text-center font-size-5 letter-spacing-1">{oneGramCoinBuy.formatted}</div>
          <div className="item-cell text-left font-size-5 letter-spacing-1">{oneGramCoinSell.formatted}</div>
        </div>
      </>
    );
  }, [
    newDesignCoinBuy,
    newDesignCoinSell,
    halfCoinBuy,
    halfCoinSell,
    quarterCoinBuy,
    quarterCoinSell,
    oneGramCoinBuy,
    oneGramCoinSell,
  ]);

  // نمایش طلاها
  const golds = useMemo(() => {
    return (
      <>
        <div className="row-item p-4p px-16p f-row justify-content-between">
          <div className="item-cell text-right font-size-5">انس طلا</div>
          <div className="item-cell text-center font-size-5 letter-spacing-1">{ouncesOfGoldBuy.formatted}</div>
          <div className="item-cell text-left font-size-5 letter-spacing-1">{ouncesOfGoldSell.formatted}</div>
        </div>

        <div className="row-item p-4p px-16p f-row justify-content-between">
          <div className="item-cell text-right font-size-5">مظنه تهران</div>
          <div className="item-cell text-center font-size-5 letter-spacing-1">{quoteTehranBuy.formatted}</div>
          <div className="item-cell text-left font-size-5 letter-spacing-1">{quoteTehranSell.formatted}</div>
        </div>

        <div className="row-item p-4p px-16p f-row justify-content-between">
          <div className="item-cell text-right font-size-5">طلای ۱۸‌عیار</div>
          <div className="item-cell text-center font-size-5 letter-spacing-1">{eighteenCaratGoldBuy.formatted}</div>
          <div className="item-cell text-left font-size-5 letter-spacing-1">{eighteenCaratGoldSell.formatted}</div>
        </div>

        {moltenGoldPrice !== 0 && prices.raten_molten.price && (
          <div className="row-item p-4p px-16p f-row justify-content-between">
            <div className="item-cell text-right font-size-5">آبشده نقدی</div>
            <div className="item-cell text-center font-size-5 letter-spacing-1">{moltenGoldPrice.formatted}</div>
            <div className="item-cell text-left font-size-5 letter-spacing-1" />
          </div>
        )}

        <div className="row-item p-4p px-16p f-row justify-content-between">
          <div className="item-cell text-right font-size-5">طلای متفرقه</div>
          <div className="item-cell text-center font-size-5 letter-spacing-1">
            {otherGoldPrice?.formatted ?? otherGoldPrice}
          </div>
          <div className="item-cell text-left font-size-5 letter-spacing-1">
            <BtnShareRates
              className="f-row justify-content-end"
              style={{ position: 'absolute', left: -15, top: -2 }}
              preparedRatesText={prepareRatesText()}
            />
          </div>
        </div>
      </>
    );
  }, [
    ouncesOfGoldBuy,
    ouncesOfGoldSell,
    ouncesOfGoldMain,
    quoteTehranBuy,
    quoteTehranSell,
    eighteenCaratGoldBuy,
    eighteenCaratGoldSell,
    otherGoldPrice,
    moltenGoldPrice,
  ]);

  const header = useMemo(() => {
    return (
      <div className="card-header all-center border-none">
        <div className="row-item f-row px-16p w-100" style={{ opacity: 0.75 }}>
          <div className="item-cell text-right font-size-5 font-weight-300">#</div>
          <div className="item-cell text-center font-size-5 letter-spacing-1 font-weight-300">قیمت خرید</div>
          <div className="item-cell text-left font-size-5 font-weight-300">قیمت فروش</div>
        </div>
      </div>
    );
  }, []);

  function prepareRatesText(addSign = true) {
    let message = 'نرخ طلا و سکه\n\n';

    // تمام سکه
    message += 'تمام سکه\n';
    message += `خرید: ${newDesignCoinBuy.formatted}\n`;
    message += `فروش: ${newDesignCoinSell.formatted}\n\n`;

    // نیم سکه
    message += 'نیم سکه\n';
    message += `خرید: ${halfCoinBuy.formatted}\n`;
    message += `فروش: ${halfCoinSell.formatted}\n\n`;

    // ربع سکه
    message += 'ربع سکه\n';
    message += `خرید: ${quarterCoinBuy.formatted}\n`;
    message += `فروش: ${quarterCoinSell.formatted}\n\n`;

    // سکه یک گرمی
    message += 'سکه یک گرمی\n';
    message += `خرید: ${oneGramCoinBuy.formatted}\n`;
    message += `فروش: ${oneGramCoinSell.formatted}\n\n`;

    // انس طلا
    message += 'انس طلا\n';
    message += `خرید: ${ouncesOfGoldBuy.formatted}\n`;
    message += `فروش: ${ouncesOfGoldSell.formatted}\n\n`;

    // مظنه تهران
    message += 'مظنه تهران\n';
    message += `خرید: ${quoteTehranBuy.formatted}\n`;
    message += `فروش: ${quoteTehranSell.formatted}\n\n`;

    // طلای ۱۸ عیار
    message += 'طلای ۱۸‌عیار\n';
    message += `خرید: ${eighteenCaratGoldBuy.formatted}\n`;
    message += `فروش: ${eighteenCaratGoldSell.formatted}\n\n`;

    // Molten Gold
    if (moltenGoldPrice !== 0) {
      message += 'آبشده نقدی\n';
      message += `خرید: ${moltenGoldPrice.formatted}\n\n`;
    }

    // Other Gold
    message += 'طلای متفرقه\n';
    message += `خرید: ${otherGoldPrice?.formatted ?? otherGoldPrice}`;

    if (addSign) {
      message += `\n\n\n${options.title}\n`;
      const rubikaLink = options.rubika_link || options.whatsapp_link;
      if (rubikaLink) message += `تماس در روبیکا: ${rubikaLink}\n`;
      if (options.instagram) message += `پیج اینستاگرام: https://instagram.com/_u/${options.instagram}\n`;
      if (options.phone) message += `تلفن: ${options.phone}\n`;
    }

    return message;
  }

  return { coins, golds, header, prepareRatesText };
};

export default useListWRates;
